// How a part puts its screens into Me. The shell imports no part: the host lists
// the parts (`createMeApp({ parts })`), the same way it lists event types.
//
// Promise: `defineScreenPart` only checks and returns the description; nothing
// is registered globally. Ids are unique within an app (checked by createMeApp).
import type { QueryKey } from "@tanstack/react-query";
import type { AnyRoute } from "@tanstack/react-router";
import type { ComponentType, ReactNode } from "react";
import type { RealtimeHandlers } from "../realtime/realtime";
import type { ShellBadge, ShellOngoing } from "../shell/outside/contract";
import type { ShortcutDef } from "../shell/shortcuts";

export interface NavItem {
  /** Smaller comes first. Inbox 10, Cases 20, Agents 30, Documents 40, People 50. */
  order: number;
  label(): string;
  icon: ComponentType<{ className?: string }>;
  /** A path under /me (e.g. "/me/cases"). */
  to: string;
  /**
   * Going there again opens what was last open under `to` on this device (a
   * detail, or the list); from under `to` itself, the list
   * (shell/nav-memory.ts).
   */
  remembers?: boolean;
  /**
   * A hook returning what to show at the end of the row: a dot or a count, only
   * when going there finds something to do. Called on every render of the nav.
   */
  useBadge?(): ReactNode;
  /**
   * The same badge as data, for a frame drawn outside the screens
   * (shell/outside). Without it a shown `useBadge` goes out as a plain dot.
   */
  useBadgeData?(): ShellBadge | null;
  /**
   * Drawn after the other rows under a line, above the favorites (the
   * cloud's direct messages, not personal data).
   */
  apart?: boolean;
}

/** A block of the Me sidebar below the nav (e.g. the labels of Case and People). */
export interface NavSection {
  order: number;
  Component: ComponentType;
}

/** How a part's `Ongoing` is drawn where it is put. */
export interface OngoingProps {
  shape: "row" | "icon";
  /** Called after going there (the phone's panel closes). */
  onGo?(): void;
}

/**
 * How the screen shows a thing of a kind this part owns: as a favorite and as
 * a reference chip (`[[kind:id|name]]`; the refs part asks the server for the
 * current name, this says how to show and open it).
 */
export interface FavoriteKind {
  icon: ComponentType<{ className?: string }>;
  /**
   * Where it opens. `path` is what the refs part answered beyond the id when
   * known (a key, the workspace's slug); favorites do not have it.
   */
  to(targetId: string, path?: Readonly<Record<string, unknown>>): string;
  /**
   * Paths of this app that name one, as the server's ref kind writes them:
   * `:id` or `:key` segments, another `:name` for any one segment, `/*` at
   * the end for any decoration (`/me/documents/:key`,
   * `/:slug/documents/:key`). A URL of one pasted into a document becomes a
   * reference.
   */
  refPaths?: readonly string[];
  /**
   * Drawn in a reference chip in place of the icon (a person's mention shows
   * their picture: who it is at a glance). `name` is the name shown.
   */
  chipMark?: ComponentType<{ id: string; name: string }>;
  /**
   * How a Waiting on one names it, when not by its own name (an item of the
   * owner's turn: the waiting's title already says what to check, so it says
   * where to answer; an Action says its Case too). `state` is the target's,
   * when its kind has one; `path` what the server answered beyond the id.
   */
  waitName?(target: {
    label: string;
    state?: string;
    path?: Readonly<Record<string, unknown>>;
  }): string;
}

/** An item of the settings screen (`/me/settings/<id>`). */
export interface SettingsItem {
  id: string;
  order: number;
  /**
   * `app`: the item sets only this app on this device (appearance, the menu
   * bar). Inside the desktop app such items stand under their own heading at
   * the end; elsewhere they stay in the one list, by `order`.
   */
  group?: "app";
  label(): string;
  /** The item's mark in the settings sidebar (all there is of it when folded). */
  icon: ComponentType<{ className?: string }>;
  /** One sentence for the settings index. */
  description(): string;
  /** Anchors inside the item (`#<id>`), found by the settings search. */
  sections?: ReadonlyArray<{ id: string; label(): string }>;
  /**
   * The item draws the whole panel itself (a list and the detail of what is
   * open in it) instead of a settings page. What is open is the path's next
   * segment (`/me/settings/<id>/<sub>`, read as `useParams().sub`).
   */
  screen?: boolean;
  /**
   * The item has pages below it (`/me/settings/<id>/<sub>`, read as
   * `useParams().sub`) in the settings' reading column: the Component draws
   * the page itself (`SettingsPage`), its own or the one open below it.
   */
  pages?: boolean;
  /**
   * Whether the person sees the item in the sidebar and the index (a hook;
   * default: always). For an item only some people may use. Its page still
   * opens by its path, so it says itself when it is not theirs.
   */
  useShown?(): boolean;
  Component: ComponentType;
}

/** What the detail says of the thing its header shows. */
export interface HeaderActionTarget {
  /** The thing the detail shows (a Case's id). */
  targetId: string;
  /**
   * What the detail hands over of that thing: `ext` of a Case; for a document
   * (kind "page") `{ page, writable }`.
   */
  ext: Readonly<Record<string, unknown>> | undefined;
}

/** What a header action of another part's detail gets. */
export interface HeaderActionProps extends HeaderActionTarget {
  /** Whether the action's item in the detail's "…" was chosen (it may show its dialog). */
  opened: boolean;
  open(): void;
  close(): void;
}

/**
 * What a part puts in the header of another part's detail, so neither imports
 * the other (the Agent chat part puts "the chat of this Case" in the Case's
 * header; cloud puts publishing by link in a document's). A button (or a mark)
 * before the star, and/or an item of the detail's "…" that opens it
 * (`opened`). It acts (goes somewhere, makes something, shows its own
 * dialog); it never opens a panel inside that detail.
 */
export interface HeaderAction {
  id: string;
  Component?: ComponentType<HeaderActionProps>;
  /**
   * The button `Component` draws, as the outside's data (shell/outside): its
   * words, icon and what pressing does; null when it draws none now. A hook,
   * called on every render of the header. Without it the button is the web's
   * own (a frame drawn outside does not get it).
   */
  useAction?(target: HeaderActionTarget): HeaderActionData | null;
  /** The item in the detail's "…" that opens it; null for none. Pure: no hooks. */
  menuItem?(target: HeaderActionTarget): { label: string } | null;
}

/** A header action's button as data (see `HeaderAction.useAction`). */
export interface HeaderActionData {
  label: string;
  icon?: string;
  /** Absent: pressing it opens the action (`opened`), as its item in the "…" does. */
  onPress?(): void;
}

/** What a row's line under its title tells of: the thing and whether it is closed. */
export interface RowNoteTarget extends HeaderActionTarget {
  /** The thing is closed (a done step): only what still moves is told. */
  closed: boolean;
}

/** What a row's button gets: the thing, and the size of the row's own buttons. */
export interface RowButtonProps extends HeaderActionTarget {
  /**
   * The row's buttons are the 26 px round ones (a Case's step in its Chain,
   * beside its "…"), not a list's small 24 px ones.
   */
  round?: boolean;
}

/**
 * What a part puts on the rows of another part's list, after the name (the
 * Agent chat part puts a chat's mark and the button to the chat on the Cases
 * list's Case and step rows, and on a Case's steps), so neither imports the
 * other. Small, one line high.
 */
export interface RowMark {
  id: string;
  /** A mark right after the name, inside the row's link (nothing to press). */
  Mark?: ComponentType<HeaderActionTarget>;
  /** A small icon button or link at the name column's end, outside the row's link. */
  Button?: ComponentType<RowButtonProps>;
  /**
   * Words at the head of the line under the title, on rows that have one (a
   * Case's step in its Chain: what its chat does). A hook, called on every
   * render of the row; null for nothing.
   */
  useNote?(target: RowNoteTarget): ReactNode;
}

/** What a side view gets: the thing to show. Its frame comes from `useSideView`. */
export interface SideViewProps {
  /** The thing's id (or key), as a reference or a change names it. */
  id: string;
}

export interface ScreenPart {
  id: string;
  nav?: NavItem;
  navSection?: NavSection;
  /** Routes under /me. `me` is the parent route. */
  routes?(me: AnyRoute): AnyRoute[];
  /**
   * Routes under /me without Me's frame (no sidebar, no header): a page a
   * host shows inside a view of its own (an item's UI alone). `me` is /me.
   */
  bareRoutes?(me: AnyRoute): AnyRoute[];
  realtime?: RealtimeHandlers;
  favoriteKinds?: Readonly<Record<string, FavoriteKind>>;
  shortcuts?: readonly ShortcutDef[];
  settings?: readonly SettingsItem[];
  /**
   * Buttons this part puts in other parts' detail headers, by the kind the
   * detail shows ("case").
   */
  headerActions?: Readonly<Record<string, readonly HeaderAction[]>>;
  /** Marks this part puts on other parts' list rows, by the rows' kind ("case", "step"). */
  rowMarks?: Readonly<Record<string, readonly RowMark[]>>;
  /**
   * How a thing of a kind this part owns is shown embedded beside another
   * screen (the Agent chat's side view), by ref kind ("case", "page",
   * "person"). The component draws its usual detail; its header gets the side
   * view's controls (open full screen, close) from the shell.
   */
  sideViews?: Readonly<Record<string, ComponentType<SideViewProps>>>;
  /**
   * Mounted once for the whole app, inside the router and the query client,
   * on every page (Me and the host's own). For what a part keeps doing while
   * the app is open and that needs the router or a query (showing OS
   * notifications, the app's badge, playing a call's sound). It draws nothing
   * but what the person must press for that work to go on (a call's "play
   * audio" when the browser refused to play by itself).
   */
  Background?: ComponentType;
  /** Quick controls in the account menu, beside appearance and language. */
  AccountQuickSettings?: ComponentType;
  /**
   * What the person can go back to while it goes on (the call they are in):
   * a row at the foot of every sidebar (`shape` "row"), an icon at the foot of
   * the folded column ("icon"), and a row at the top of the phone's panel
   * (with `onGo`, which closes the panel). Draws nothing when nothing goes on.
   */
  Ongoing?: ComponentType<OngoingProps>;
  /** The same as data (`ShellOngoing`), for a host that draws the sidebar; null while nothing goes on. */
  useOngoingData?(): ShellOngoing | null;
  /**
   * Which of this part's queries cloud keeps across reloads: only what is needed
   * to draw the last screen without waiting. Never unread state.
   */
  persist?(key: QueryKey): boolean;
}

export function defineScreenPart(part: ScreenPart): ScreenPart {
  if (!/^[a-z][a-z0-9-]*$/.test(part.id))
    throw new Error(`screen part id must be kebab-case: ${part.id}`);
  return part;
}
